import { useCallback, useEffect, useRef, useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import type { OrbitRoomPerson, OrbitRoomPurpose, OrbitRoomView } from '../../types/aai';

/*
 * One read of the Room for a purpose and scope. The first read for a company
 * also asks the server to turn the ledger's gaps into open questions, once, so
 * a Room never opens on an empty New to Ask when the file has gaps.
 */

const generated = new Set<string>();

export const useOrbitRoom = (clientId: string, purpose: OrbitRoomPurpose, scope: string) => {
  const [view, setView] = useState<OrbitRoomView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        if (!generated.has(clientId)) {
          generated.add(clientId);
          try {
            await aaiApi.generateOrbitAsks('all');
          } catch {
            // A viewer without the open grant cannot generate; the read below still answers.
          }
        }
        const res = await aaiApi.getOrbitRoom(purpose, scope);
        if (live) setView(res.data);
      } catch {
        if (live) setError('Could not load the Orbit Room.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [clientId, purpose, scope, tick]);

  return { view, loading, error, reload };
};

export const usePeople = (enabled: boolean) => {
  const [people, setPeople] = useState<OrbitRoomPerson[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    aaiApi.getOrbitRoomPeople()
      .then((res) => { if (live) setPeople(res.data.people); })
      .catch(() => { if (live) setPeople([]); });
    return () => { live = false; };
  }, [enabled]);
  return people;
};
